// Static batching: bake every static mesh under a root into one mesh per (material, shadow) pair.
// The Ministry is ~1,000 small meshes (walls, merlons, props, trees); drawn one by one that was
// thousands of draw calls a frame (shadow pass included). Merged, it is a few dozen.
//
// Plain-coloured toon surfaces go further: their colour is baked into a vertex colour and they share
// one "palette" material per group, so the ~150 flat colours of the walls, trims and props draw in a
// handful of calls instead of one each. The result is identical: white × vertex colour = the colour.
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

interface Piece {
  geo: THREE.BufferGeometry;
  material: THREE.Material;
  cast: boolean;
  /** Palette group, when the piece's colour was baked into its vertices. */
  palette?: string;
}

export interface MergeOptions {
  /** Meshes (and their subtrees) to leave alone. */
  keep?: Set<THREE.Object3D>;
  /**
   * Palette group of a plain-coloured toon material (surfaces in different groups keep different
   * shaders, e.g. the ground's baked occlusion); null keeps the material as it is.
   */
  paletteGroup?: (m: THREE.Material) => string | null;
}

/** A standalone geometry holding only the triangles [start, start+count) of `src`, re-indexed compactly. */
function extract(src: THREE.BufferGeometry, start: number, count: number, color?: THREE.Color): THREE.BufferGeometry {
  const index = src.index;
  const names = (color ? ['position', 'normal'] : ['position', 'normal', 'uv']).filter((n) => src.attributes[n]);
  const remap = new Map<number, number>();
  const order: number[] = [];
  const idx: number[] = [];
  const end = Math.min(start + count, index ? index.count : src.attributes.position!.count);
  for (let i = start; i < end; i++) {
    const v = index ? index.getX(i) : i;
    let n = remap.get(v);
    if (n === undefined) {
      n = order.length;
      remap.set(v, n);
      order.push(v);
    }
    idx.push(n);
  }
  const out = new THREE.BufferGeometry();
  for (const name of names) {
    const a = src.attributes[name] as THREE.BufferAttribute;
    const arr = new Float32Array(order.length * a.itemSize);
    for (let k = 0; k < order.length; k++) for (let c = 0; c < a.itemSize; c++) arr[k * a.itemSize + c] = a.getComponent(order[k]!, c);
    out.setAttribute(name, new THREE.BufferAttribute(arr, a.itemSize));
  }
  if (color) {
    const arr = new Float32Array(order.length * 3);
    for (let k = 0; k < order.length; k++) color.toArray(arr, k * 3);
    out.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  }
  out.setIndex(order.length > 65535 ? new THREE.Uint32BufferAttribute(idx, 1) : new THREE.Uint16BufferAttribute(idx, 1));
  return out;
}

/** A toon material whose look is fully described by its colour (so it can become a vertex colour). */
function plainToon(m: THREE.Material): m is THREE.MeshToonMaterial {
  const t = m as THREE.MeshToonMaterial;
  return (
    t.isMeshToonMaterial === true &&
    !t.map &&
    !t.alphaMap &&
    !t.emissiveMap &&
    !t.lightMap &&
    !t.aoMap &&
    !t.bumpMap &&
    !t.normalMap &&
    !t.displacementMap &&
    !t.vertexColors &&
    !t.transparent &&
    t.opacity === 1 &&
    t.alphaTest === 0 &&
    (t.emissiveIntensity === 0 || t.emissive.getHex() === 0)
  );
}

/**
 * Merge all meshes under `root` (except those kept or flagged `userData.dynamic`) and replace them
 * with batched meshes added to `root`. Returns the number of meshes merged and the palette material
 * created for each group.
 */
export function mergeStatic(root: THREE.Object3D, opts: MergeOptions = {}): { merged: number; palettes: Map<string, THREE.MeshToonMaterial> } {
  const keep = opts.keep ?? new Set<THREE.Object3D>();
  root.updateMatrixWorld(true);
  const inverseRoot = root.matrixWorld.clone().invert();
  const pieces: Piece[] = [];
  const victims: THREE.Mesh[] = [];
  const palettes = new Map<string, THREE.MeshToonMaterial>();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || keep.has(m) || m.userData.dynamic || !m.visible) return;
    for (let p: THREE.Object3D | null = m.parent; p && p !== root; p = p.parent) if (keep.has(p) || p.userData.dynamic || !p.visible) return;
    const geo = m.geometry;
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    const matrix = new THREE.Matrix4().multiplyMatrices(inverseRoot, m.matrixWorld);
    const groups = Array.isArray(m.material) && geo.groups.length ? geo.groups : [{ start: 0, count: geo.index ? geo.index.count : geo.attributes.position!.count, materialIndex: 0 }];
    for (const g of groups) {
      const mat = mats[g.materialIndex ?? 0];
      if (!mat) continue;
      const group = geo.attributes.normal && plainToon(mat) ? (opts.paletteGroup?.(mat) ?? null) : null;
      const piece = extract(geo, g.start, g.count, group === null ? undefined : (mat as THREE.MeshToonMaterial).color);
      piece.applyMatrix4(matrix);
      pieces.push({ geo: piece, material: mat, cast: m.castShadow, ...(group === null ? {} : { palette: `${group}|${mat.side}` }) });
    }
    victims.push(m);
  });
  for (const m of victims) m.removeFromParent();

  const buckets = new Map<string, Piece[]>();
  for (const p of pieces) {
    const key = p.palette ? `palette:${p.palette}|${p.cast ? 1 : 0}` : `${p.material.uuid}|${p.cast ? 1 : 0}|${p.geo.attributes.uv ? 1 : 0}`;
    let list = buckets.get(key);
    if (!list) buckets.set(key, (list = []));
    list.push(p);
  }
  for (const list of buckets.values()) {
    const geos = list.map((p) => p.geo);
    // Index widths must match across a merge.
    if (geos.some((g) => g.index instanceof THREE.Uint32BufferAttribute)) {
      for (const g of geos) if (!(g.index instanceof THREE.Uint32BufferAttribute)) g.setIndex(new THREE.Uint32BufferAttribute(Array.from(g.index!.array), 1));
    }
    const merged = mergeGeometries(geos, false);
    for (const g of geos) g.dispose();
    if (!merged) continue;
    merged.computeBoundingSphere();
    const first = list[0]!;
    let material = first.material;
    if (first.palette) {
      const group = first.palette.slice(0, first.palette.lastIndexOf('|'));
      let pal = palettes.get(first.palette);
      if (!pal) {
        // The group's shader with a white base colour: the vertex colours carry each surface's own.
        pal = (first.material as THREE.MeshToonMaterial).clone();
        pal.color.set(0xffffff);
        pal.vertexColors = true;
        pal.name = `palette:${group}`;
        palettes.set(first.palette, pal);
      }
      material = pal;
    }
    const mesh = new THREE.Mesh(merged, material);
    mesh.castShadow = first.cast;
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    mesh.name = 'static-batch';
    root.add(mesh);
  }
  return { merged: victims.length, palettes };
}
